import { useState, useEffect, useCallback } from 'react'
import { authFetch } from '../../lib/api'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'

interface TestConfig {
  id: string
  name: string
  command: string
  run_on: string
}

interface TestRun {
  status: 'pass' | 'fail'
  output: string
  duration_ms: number
  created_at: string
}

export function ValidationPanel() {
  const project = usePulseMemory(s => s.memory.project) || 'default'
  const [configs, setConfigs] = useState<TestConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [running, setRunning] = useState<string | null>(null)
  const [lastRun, setLastRun] = useState<Record<string, TestRun | null>>({})
  const [form, setForm] = useState({ name: '', command: '', runOn: 'manual' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await authFetch(`/api/validation/${encodeURIComponent(project)}`)
      if (r.ok) setConfigs(await r.json())
    } catch {}
    setLoading(false)
  }, [project])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    if (!form.name || !form.command) return
    await authFetch('/api/validation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project, name: form.name, command: form.command, runOn: form.runOn }),
    })
    setForm({ name: '', command: '', runOn: 'manual' })
    setCreating(false)
    load()
  }

  const handleRun = async (configId: string) => {
    setRunning(configId)
    try {
      const r = await authFetch(`/api/validation/run/${configId}`, { method: 'POST' })
      if (r.ok) {
        const result = await r.json()
        setLastRun(prev => ({ ...prev, [configId]: result }))
      }
    } catch {}
    setRunning(null)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this test config?')) return
    await authFetch(`/api/validation/${id}`, { method: 'DELETE' })
    load()
  }

  const s: React.CSSProperties = { padding: 20, height: '100%', overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-1)' }
  const card: React.CSSProperties = { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8 }
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }
  const input: React.CSSProperties = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }

  return (
    <div style={s}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--accent)' }}>🧪 Validation / CI Tests</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 11 }}>
        Configure and run tests for: <strong>{project}</strong>
      </p>

      {!creating ? (
        <button style={{ ...btn, marginBottom: 16, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={() => setCreating(true)}>
          + Add Test
        </button>
      ) : (
        <div style={{ ...card, marginBottom: 16 }}>
          <input style={{ ...input, marginBottom: 8 }} placeholder="Test name (e.g. Unit Tests)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input style={{ ...input, marginBottom: 8 }} placeholder="Command (e.g. npm test)" value={form.command} onChange={e => setForm({ ...form, command: e.target.value })} />
          <select style={{ ...input, marginBottom: 8, appearance: 'auto' as any }} value={form.runOn} onChange={e => setForm({ ...form, runOn: e.target.value })}>
            <option value="manual">Manual only</option>
            <option value="save">On save</option>
            <option value="deploy">On deploy</option>
            <option value="both">On save + deploy</option>
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={handleCreate}>Create</button>
            <button style={btn} onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: 'var(--text-2)' }}>Loading...</div> : (
        configs.length === 0 ? <div style={{ color: 'var(--text-2)' }}>No tests configured. Add one above.</div> : (
          configs.map(c => {
            const run = lastRun[c.id]
            return (
              <div key={c.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14 }}>{run ? (run.status === 'pass' ? '✅' : '❌') : '⬜'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 11 }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{c.command} · {c.run_on}</div>
                  </div>
                  <button style={{ ...btn, fontSize: 10 }} onClick={() => handleRun(c.id)} disabled={running === c.id}>
                    {running === c.id ? '⏳' : '▶ Run'}
                  </button>
                  <button onClick={() => handleDelete(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4 }}>🗑️</button>
                </div>
                {run && (
                  <div style={{ marginTop: 6, padding: 8, background: 'var(--bg-1)', borderRadius: 4, fontSize: 10, maxHeight: 120, overflow: 'auto' }}>
                    <div style={{ marginBottom: 4, color: run.status === 'pass' ? '#5fd4a4' : '#f07070' }}>
                      {run.status === 'pass' ? 'PASSED' : 'FAILED'} in {run.duration_ms}ms
                    </div>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--text-2)' }}>{run.output}</pre>
                  </div>
                )}
              </div>
            )
          })
        )
      )}
    </div>
  )
}
